import React, { useRef, useState, createContext, useContext, useEffect, useCallback } from 'react';
import { useToast, useToastRegion } from '@react-aria/toast';
import { useToastState, ToastState } from '@react-stately/toast';
import { IconButton } from '../../atoms/icon-button/IconButton';
import { Typography } from '../../atoms/typography/Typography';
import { ToastProps, ToastProviderProps, ToastContextValue, ToastVariant, ToastContent } from './toast.types';
import * as S from './toast.styles';

// Create a context for the toast
const ToastContext = createContext<ToastContextValue | undefined>(undefined);

/**
 * Toast provider component that provides the toast functionality
 */
export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const state = useToastState<Required<ToastContent>>({ maxVisibleToasts: 5 });

  const showToast = useCallback(
    (props: ToastContent) => {
      const { title, message, variant, duration, closable, onClose } = props;

      return state.add({
        title,
        message,
        variant: variant || 'info',
        duration: duration || 5000,
        closable: closable || true,
        onClose: onClose || (() => {}),
      });
    },
    [state]
  );

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {state.visibleToasts.length > 0 && <ToastRegion state={state} />}
    </ToastContext.Provider>
  );
};

/**
 * Hook to use the toast functionality
 */
export const useToastContext = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToastContext must be used within a ToastProvider');
  }
  return context;
};

/**
 * Toast region component that creates the landmark region
 */
const ToastRegion = ({ state, className }: { state: ToastState<Required<ToastContent>>; className?: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const { regionProps } = useToastRegion({}, state, ref);

  return (
    <S.ToastContainer className={className} {...regionProps} ref={ref}>
      {state.visibleToasts.map((toast) => (
        <Toast key={toast.key} toast={toast} state={state} />
      ))}
    </S.ToastContainer>
  );
};

/**
 * Individual toast item component
 */
const Toast = (props: ToastProps) => {
  const { toast, state } = props;
  const { title, message, variant, duration, closable, onClose } = toast.content;

  const ref = useRef<HTMLDivElement>(null);
  const [isClosing, setIsClosing] = useState(false);
  const animationRef = useRef<NodeJS.Timeout | null>(null);

  const { toastProps, titleProps, contentProps, closeButtonProps } = useToast({ toast }, state, ref);

  // Use useCallback to memoize the handleClose function
  const handleClose = useCallback(() => {
    if (isClosing) return;

    // 1. Set closing state for animation
    setIsClosing(true);

    // 2. Wait for animation to complete before removing
    animationRef.current = setTimeout(() => {
      // 3. Only after animation completes, tell state to close
      state.close(toast.key);
      if (onClose) onClose();
    }, 300); // Match animation duration in CSS
  }, [isClosing, state, toast.key, onClose]);

  // Handle auto-dismiss based on duration
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (duration !== Infinity) {
      timer = setTimeout(() => {
        handleClose();
      }, duration);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [duration, handleClose]);

  // Clean up timers
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        clearTimeout(animationRef.current);
      }
    };
  }, []);

  // Variant to icon mapping
  const getIconForVariant = (variant: ToastVariant) => {
    switch (variant) {
      case 'success':
        return '😊'; // Smile emoji for success
      case 'warning':
        return '🤔'; // Warning emoji for warning
      case 'error':
        return '😔'; // Cross emoji for error
      case 'info':
      default:
        return '✨'; // Info emoji for info
    }
  };

  return (
    <S.Toast className={isClosing ? 'closing' : ''} {...toastProps} ref={ref}>
      <S.ToastHeader>
        <S.ToastTitle $taostVariant={variant} variant="body-16" as="h3" {...titleProps}>
          {title} {getIconForVariant(variant)}
        </S.ToastTitle>
        {closable && (
          <IconButton
            {...closeButtonProps}
            icon="close"
            variant="circle"
            size={16}
            aria-label="Close toast"
            onPress={handleClose}
          />
        )}
      </S.ToastHeader>
      <S.ToastContent {...contentProps}>
        <Typography variant="body-12" as="p">
          {message}
        </Typography>
      </S.ToastContent>
    </S.Toast>
  );
};

export { ToastRegion };
